import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PlaceSopDialog from './PlaceSopDialog';
import { getPlaceSops, deletePersonSop } from '../../services/userService';
import { SOP_GRID_COLUMNS } from '../../constants/placeSopsTabConstants';

const PlaceSopsTab = ({ ptsPlaceID, onSopsChanged }) => {
  const dispatch = useDispatch();
  const canEditPlace = getFullPermissions('globals', 'Places', 'any').Edit;
  const [loading, setLoading] = useState(false);
  const [sopLinks, setSopLinks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSopLink, setEditingSopLink] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadSopLinks = useCallback(async () => {
    if (!ptsPlaceID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPlaceSops(ptsPlaceID),
      onSuccess: (data) => setSopLinks(data),
    });
  }, [dispatch, ptsPlaceID]);

  const existingSopIds = useMemo(() => sopLinks.map((link) => link.ptsSOPID), [sopLinks]);

  const handleAdd = useCallback(() => {
    setEditingSopLink(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingSopLink(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingSopLink(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadSopLinks();
    onSopsChanged?.();
  }, [handleFormClose, loadSopLinks, onSopsChanged]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove this "${row.SOP?.SOPID || 'SOP'}" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () =>
              deletePersonSop({
                ptsParentID: ptsPlaceID,
                ParentType: 'Place',
                ptsSOPID: row.ptsSOPID,
              }),
            onSuccess: () => {
              dispatch(notify('SOP deleted successfully', 'success'));
              loadSopLinks();
              onSopsChanged?.();
            },
          });
        },
        'Delete SOP'
      );
    },
    [dispatch, ptsPlaceID, requestConfirm, loadSopLinks, onSopsChanged]
  );

  useEffect(() => {
    loadSopLinks();
  }, [loadSopLinks]);

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add SOP"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPlace}
        />
      </Box>

      <CommonDataGrid
        rows={sopLinks}
        columns={SOP_GRID_COLUMNS}
        getRowId={(row) => row.ptsSOPID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Place SOPs',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPlace },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPlace },
        }}
      />

      {formOpen && (
        <PlaceSopDialog
          open={formOpen}
          ptsPlaceID={ptsPlaceID}
          sopLink={editingSopLink}
          existingSopIds={existingSopIds}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PlaceSopsTab;
